"use client";

import {
  useWeb3ModalAccount,
  useWeb3ModalProvider,
} from "@web3modal/ethers/react";
import { ethers } from "ethers";
import { useSession } from "next-auth/react";
import { useCallback, useEffect, useState } from "react";
import { AaHandler } from "~/utils/web3/aa/aaHandler";
import {
  approveUserOperation,
  sendUserOperationEth,
  sendUserOperationWithApprove,
} from "~/utils/web3/aa/aaUtils";
import { getGasObjectFromProvider } from "~/utils/web3/gas";
import {
  makeTransaction,
  sendEOATransactionWithGasObject,
} from "~/utils/web3/eoa/transaction";
import { getChainIdFromProvider } from "~/utils/web3/network";
import { useAuth } from "./useAuth";
import { env } from "~/env";
import { waitForTransaction } from "~/utils/web3/transaction";

export type WalletType = "EOA" | "AA" | null;

export const useWallet = () => {
  const { data: _session } = useSession();
  const { walletProvider } = useWeb3ModalProvider();
  const { web3modalLogin } = useAuth();

  const [walletType, setWalletType] = useState<WalletType | null>(null);
  const [address, setAddress] = useState<string>();
  const [aaHandler, setAaHandler] = useState<AaHandler | null>(null);
  const [eoaSigner, setEoaSigner] = useState<ethers.JsonRpcSigner | null>(null);

  const _getPrivateKeyFromLocalStorage = () => {
    const privateKey = localStorage.getItem("privateKey");
    console.log({ privateKey });
    if (!privateKey) throw new Error("Private key not found");
    if (privateKey.length === 64) return "0x" + privateKey;
    return privateKey;
  };

  const _verifySessionWalletExists = useCallback(() => {
    if (!_session) throw new Error("Session not found");
    if (!_session?.user) throw new Error("User not found");
    if (!_session?.user.loginType) throw new Error("Wallet type not found");
    return _session;
  }, [_session]);

  const _verifyWalletProvider = useCallback(() => {
    if (!walletProvider) return null;
    // return walletProvider;

    return new ethers.BrowserProvider(walletProvider);
  }, [walletProvider]);

  const _verifyEoaSigner = useCallback(() => {
    if (!eoaSigner) throw new Error("EOA signer not found");
    return eoaSigner;
  }, [eoaSigner]);

  const _verifyAaHandler = useCallback(() => {
    if (!aaHandler) throw new Error("AA handler not found");
    return aaHandler;
  }, [aaHandler]);

  const _verifyDeployedWallet = useCallback(async () => {
    const handler = _verifyAaHandler();
    if (!(await handler.isWalletDeployed()))
      throw new Error("Wallet not deployed");
  }, [_verifyAaHandler]);

  const _deployWallet = useCallback(async () => {
    console.log("Deploying wallet...");
    const provider = new ethers.JsonRpcProvider(
      env.NEXT_PUBLIC_NETWORK_RPC_URL,
    );
    const handler = _verifyAaHandler();
    const deployTxHash = await handler.deployWallet();

    if (!deployTxHash) throw new Error("Deploy transaction failed");

    const receipt = await waitForTransaction(provider, deployTxHash);

    if (!receipt?.status) throw new Error("Deploy transaction failed");

    console.log("Wallet deployed", deployTxHash);

    return receipt;
  }, [_verifyAaHandler]);

  useEffect(() => {
    const session = _verifySessionWalletExists();

    if (session.user.loginType === "WEB3_AUTH") {
      const privateKey = _getPrivateKeyFromLocalStorage();

      void AaHandler.create(privateKey).then((aaHandler) => {
        setWalletType("AA");
        setAaHandler(aaHandler);
      });
    }
    if (session.user.loginType === "WEB3_MODAL") {
      const provider = _verifyWalletProvider();
      if (!provider) {
        void web3modalLogin();
        return;
      }

      void provider.getSigner().then((signer) => {
        setWalletType("EOA");
        setEoaSigner(signer);
      });
    }
  }, [_verifySessionWalletExists, _verifyWalletProvider]);

  const getNetwork = useCallback(async () => {
    if (walletType === "EOA") {
      const signer = _verifyEoaSigner();
      return await signer.provider?.getNetwork();
    }

    if (walletType === "AA") {
      const provider = new ethers.JsonRpcProvider(
        env.NEXT_PUBLIC_NETWORK_RPC_URL,
      );

      return await provider.getNetwork();
    }
    return null;
  }, [_verifyEoaSigner, walletType]);

  const verifySessionWallet = useCallback(() => {
    if (address !== _session?.user.walletAddress)
      throw new Error("Please connect to the correct wallet");
  }, [_session, address]);

  const verifyDpPoolNetwork = useCallback(async () => {
    if (walletType === "EOA") {
      const provider = new ethers.JsonRpcProvider(
        env.NEXT_PUBLIC_NETWORK_RPC_URL,
      );

      const chainId = await getChainIdFromProvider(provider);

      const getNetworkResult = await getNetwork();

      if (getNetworkResult?.chainId !== chainId)
        throw new Error("Please connect to the correct network");
    }
  }, [getNetwork, walletType]);

  useEffect(() => {
    if (walletType === "EOA") setAddress(eoaSigner?.address);
    if (walletType === "AA") setAddress(aaHandler?.getWalletAddress());
  }, [aaHandler, eoaSigner, walletType]);

  const sendAATransaction = async (
    toList: string[],
    calldataList: string[],
  ) => {
    const handler = _verifyAaHandler();
    try {
      await _verifyDeployedWallet();
    } catch (e) {
      console.log(e);
      await _deployWallet();
    }

    console.log([...toList], [...calldataList]);

    return await sendUserOperationWithApprove(
      handler,
      [...toList],
      [...calldataList],
    );
  };

  const sendAATransactionEth = async (to: string, value: bigint) => {
    const handler = _verifyAaHandler();
    try {
      await _verifyDeployedWallet();
    } catch (e) {
      console.log(e);
      await _deployWallet();
    }

    return await sendUserOperationEth(handler, to, value);
  };

  const sendApproveUserOperation = async () => {
    const handler = _verifyAaHandler();
    try {
      await _verifyDeployedWallet();
    } catch (e) {
      console.log(e);
      await _deployWallet();
    }

    return await approveUserOperation(handler);
  };

  const sendEOATransactionEth = async (to: string, value: bigint) => {
    if (walletType !== "EOA") throw new Error("Wallet type is not EOA");

    const signer = _verifyEoaSigner();

    const [gasObject, chainId] = await Promise.all([
      getGasObjectFromProvider(signer.provider),
      getChainIdFromProvider(signer.provider),
    ]);

    const tx = makeTransaction({
      chainId: Number(chainId),
      to,
      from: address,
      value: value,
    });

    const resp = await sendEOATransactionWithGasObject(signer, tx, gasObject);
    return resp.hash;
  };

  const sendTransaction = async (to: string, calldata: string) => {
    if (walletType === "EOA") {
      const signer = _verifyEoaSigner();

      const [gasObject, chainId] = await Promise.all([
        getGasObjectFromProvider(signer.provider),
        getChainIdFromProvider(signer.provider),
      ]);

      const tx = makeTransaction({
        chainId: Number(chainId),
        to,
        from: address,
        data: calldata,
      });

      const resp = await sendEOATransactionWithGasObject(signer, tx, gasObject);
      return resp.hash;
    }

    if (walletType === "AA") {
      return await sendAATransaction([to], [calldata]);
    }
  };

  return {
    walletType,
    address,
    sendTransaction,
    sendAATransaction,
    getNetwork,
    verifySessionWallet,
    verifyDpPoolNetwork,
    sendAATransactionEth,
    sendApproveUserOperation,
    sendEOATransactionEth,
  };
};
